"use client";

// #432: out-of-state shipping selector at SIGNUP, built to the approved mockup
// SIGNUP_OUT_OF_STATE_SHIPPING_APPROVED.html. Same three methods + airport picker
// as the #431 checkout selector (shares the ShippingMethod type + us-airports
// data), but with NO prices (no cart yet) - pricing DESCRIPTORS only. AIRPORT is
// default; the airport must be actively confirmed before the step can continue.

import { useMemo, useState } from "react";
import type { ShippingMethod } from "@/lib/delivery/out-of-state-pricing";
import { searchAirports, type UsAirport } from "@/lib/delivery/us-airports";
import { abbreviateAirportName } from "@/lib/delivery/airport-label";

export type SignupShippingState = {
  method: ShippingMethod;
  airport: { iata: string; name: string } | null;
  airportConfirmed: boolean;
};

type Copy = {
  heading: string;
  sub: string;
  doorTitle: string; doorTag: string; doorDesc: string;
  airportTitle: string; airportTag: string; airportDesc: string;
  airportLabel: string; airportHint: string;
  ownTitle: string; ownTag: string; ownDesc: string;
  searchPlaceholder: string; suggested: string;
};

// MKT-#136: exported so the pre-approval no-price guard can enumerate these
// CLIENT-RENDERED strings directly - they are absent from the server HTML, so a
// crawl of the deployed /sign-up page never sees them.
export const SIGNUP_SHIPPING_COPY: Record<"en" | "es", Copy> = {
  en: {
    heading: "How should we get your flowers to you?",
    sub: "You're outside Florida — pick the option that fits you. You'll see the exact cost at checkout.",
    doorTitle: "Ship to my door",
    doorTag: "Best for smaller orders",
    doorDesc: "We ship straight to your address. Cost shown at checkout.",
    airportTitle: "Pick up at my local airport",
    airportTag: "Best for larger orders",
    airportDesc: "We fly it to your nearest airport and you collect it. Cost shown at checkout.",
    airportLabel: "Your pickup airport",
    airportHint: "We suggested your closest airport — confirm it or pick another to continue.",
    ownTitle: "Use my own carrier",
    ownTag: "If you already have a Miami trucker",
    ownDesc: "We hand your boxes to your carrier in Miami.",
    searchPlaceholder: "Search by airport code or city",
    suggested: "Suggested",
  },
  es: {
    heading: "¿Cómo quieres que te enviemos tus flores?",
    sub: "Estás fuera de Florida — elige la opción que mejor te convenga. Verás el costo exacto en el checkout.",
    doorTitle: "Enviar a mi puerta",
    doorTag: "Mejor para pedidos pequeños",
    doorDesc: "Enviamos directo a tu dirección. El costo se muestra en el checkout.",
    airportTitle: "Recoger en mi aeropuerto local",
    airportTag: "Mejor para pedidos grandes",
    airportDesc: "Lo enviamos por avión a tu aeropuerto más cercano y tú lo recoges. El costo se muestra en el checkout.",
    airportLabel: "Tu aeropuerto de recogida",
    airportHint: "Sugerimos tu aeropuerto más cercano — confírmalo o elige otro para continuar.",
    ownTitle: "Usar mi propio transportista",
    ownTag: "Si ya tienes un transportista en Miami",
    ownDesc: "Entregamos tus cajas a tu transportista en Miami.",
    searchPlaceholder: "Busca por código de aeropuerto o ciudad",
    suggested: "Sugerido",
  },
};

function Icon({ name, className }: { name: "store" | "plane" | "truck" | "chevron" | "search"; className?: string }) {
  const paths: Record<string, string> = {
    store: "M3 21l18 0 M3 7v1a3 3 0 0 0 6 0v-1m0 1a3 3 0 0 0 6 0v-1m0 1a3 3 0 0 0 6 0v-1h-18l2 -4h14l2 4 M5 21v-10.15 M19 21v-10.15 M9 21v-4a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v4",
    plane: "M16 10h4a2 2 0 0 1 0 4h-4l-4 7h-3l2 -7h-4l-2 2h-3l2 -4l-2 -4h3l2 2h4l-2 -7h3z",
    truck: "M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0 M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0 M5 17h-2v-11a1 1 0 0 1 1 -1h9v12m-4 0h6m4 0h2v-6h-8m0 -5h5l3 5",
    chevron: "M6 9l6 6l6 -6",
    search: "M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0 M21 21l-6 -6",
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {paths[name].split(" M").map((seg, i) => (
        <path key={i} d={i === 0 ? seg : `M${seg}`} />
      ))}
    </svg>
  );
}

function Radio({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={on
        ? "mt-1 h-4 w-4 flex-none rounded-full border-[5px] border-brand-teal"
        : "mt-1 h-4 w-4 flex-none rounded-full border-2 border-brand-border-strong"}
    />
  );
}

export function SignupShippingSelector({
  locale = "en",
  value,
  onChange,
}: {
  locale?: "en" | "es";
  value: SignupShippingState;
  onChange: (next: SignupShippingState) => void;
}) {
  const t = SIGNUP_SHIPPING_COPY[locale];
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchAirports(query), [query]);

  const select = (method: ShippingMethod) => onChange({ ...value, method });
  const chooseAirport = (a: UsAirport) => {
    onChange({ ...value, method: "AIRPORT", airport: { iata: a.iata, name: a.name }, airportConfirmed: true });
    setPickerOpen(false);
    setQuery("");
  };

  const card = (
    method: ShippingMethod,
    icon: "store" | "plane" | "truck",
    title: string,
    tag: string,
    desc: string,
    testid: string,
    children?: React.ReactNode,
  ) => {
    const sel = value.method === method;
    return (
      <div
        data-testid={testid}
        className={"rounded-xl p-3.5 " + (sel ? "border-2 border-brand-teal" : "border border-brand-border bg-white")}
      >
        <button type="button" aria-pressed={sel} onClick={() => select(method)} className="flex w-full items-start gap-3.5 text-left">
          <Icon name={icon} className={"mt-0.5 h-5 w-5 flex-none " + (sel ? "text-brand-teal" : "text-brand-text-muted")} />
          <span className="flex-1">
            <span className="flex items-baseline justify-between gap-2">
              <span className="text-[15px] font-semibold text-brand-text">{title}</span>
              <span className="text-xs text-brand-text-muted">{tag}</span>
            </span>
            <span className="mt-1 block text-[13px] text-brand-text-muted">{desc}</span>
          </span>
          <Radio on={sel} />
        </button>
        {children}
      </div>
    );
  };

  return (
    <div data-testid="signup-shipping-selector">
      <p className="mb-1 text-lg font-semibold text-brand-text">{t.heading}</p>
      <p className="mb-4 text-sm text-brand-text-muted">{t.sub}</p>

      <div className="flex flex-col gap-3">
        {card("DOOR", "store", t.doorTitle, t.doorTag, t.doorDesc, "signup-ship-door")}

        {card(
          "AIRPORT", "plane", t.airportTitle, t.airportTag, t.airportDesc, "signup-ship-airport",
          value.method === "AIRPORT" ? (
            <div className="ml-9 mt-3 border-t border-brand-border pt-3">
              <label className="mb-1.5 block text-[13px] text-brand-text-muted">{t.airportLabel}</label>
              <button
                type="button"
                data-testid="signup-airport-toggle"
                data-confirmed={value.airportConfirmed ? "true" : "false"}
                onClick={() => setPickerOpen((o) => !o)}
                className="flex h-9 w-full items-center gap-2 rounded-md border border-brand-border-strong bg-brand-surface px-3 text-[13px] text-brand-text"
              >
                <Icon name="search" className="h-3.5 w-3.5 flex-none text-brand-text-muted" />
                {/* #445: truncate the (now much longer) airport name to ONE line
                    so it can't overflow the field + overlap the label/hint. Full
                    name stays in the dropdown + stored value. */}
                <span className="min-w-0 flex-1 truncate text-left">
                  {value.airport
                    ? `${value.airport.iata} — ${abbreviateAirportName(value.airport.name)}`
                    : t.airportLabel}
                </span>
                {value.airport && !value.airportConfirmed ? (
                  <span className="flex-none text-brand-text-muted">· {t.suggested}</span>
                ) : null}
                <Icon name="chevron" className="h-3.5 w-3.5 flex-none text-brand-text-muted" />
              </button>
              {pickerOpen && (
                <div data-testid="signup-airport-picker" className="mt-2 rounded-md border border-brand-border bg-white p-2 shadow-sm">
                  <input
                    type="text" autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder={t.searchPlaceholder} data-testid="signup-airport-search"
                    className="mb-2 w-full rounded border border-brand-border px-2.5 py-1.5 text-[13px]"
                  />
                  <ul className="max-h-56 overflow-y-auto">
                    {results.map((a) => (
                      <li key={a.iata}>
                        <button
                          type="button" data-testid="signup-airport-option" data-iata={a.iata}
                          onClick={() => chooseAirport(a)}
                          className="flex w-full items-baseline gap-2 rounded px-2 py-1.5 text-left text-[13px] hover:bg-brand-surface"
                        >
                          <span className="font-semibold text-brand-text">{a.iata}</span>
                          <span className="text-brand-text-muted">{a.city}, {a.state} — {a.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="mt-1.5 text-xs text-brand-text-muted">{t.airportHint}</p>
            </div>
          ) : null,
        )}

        {card("OWN_CARRIER", "truck", t.ownTitle, t.ownTag, t.ownDesc, "signup-ship-own")}
      </div>
    </div>
  );
}
